/**
 * One sortable Kanban card, memoized: a drop-preview change during a drag
 * re-renders the board shell, but cards whose inputs did not change are
 * skipped, so only the placeholder and FLIP work hit the frame.
 */

import { memo } from 'react';
import { Checkbox } from '../../atoms/Checkbox';
import { KanbanSortableItem } from './KanbanSortableItem';
import type {
  KanbanDensity,
  KanbanItemBase,
  KanbanProps,
} from './Kanban.config.types';

export interface KanbanCardItemProps<TItem extends KanbanItemBase> {
  item: TItem;
  renderCard: KanbanProps<TItem>['renderCard'];
  density: KanbanDensity;
  columnItemIds: string[];
  cardDragEnabled: boolean;
  selectionEnabled: boolean;
  isItemSelectable?: (item: TItem) => boolean;
  selectedIds: string[];
  onSelectedIdsChange?: (ids: string[]) => void;
  isDragSourceCollapsed?: boolean;
  flipFrom?: string;
}

function KanbanCardItemInner<TItem extends KanbanItemBase>({
  item,
  renderCard,
  density,
  columnItemIds,
  cardDragEnabled,
  selectionEnabled,
  isItemSelectable,
  selectedIds,
  onSelectedIdsChange,
  isDragSourceCollapsed,
  flipFrom,
}: KanbanCardItemProps<TItem>) {
  const isSelected = selectedIds.includes(item.id);
  const itemSelectable =
    selectionEnabled && (!isItemSelectable || isItemSelectable(item));
  const toggleSelect = () => {
    if (!itemSelectable) return;
    onSelectedIdsChange?.(
      isSelected
        ? selectedIds.filter((id) => id !== item.id)
        : [...selectedIds, item.id]
    );
  };
  const selectionControl = itemSelectable ? (
    <div
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <Checkbox
        size="sm"
        shape="circle"
        checked={isSelected}
        onChange={toggleSelect}
        aria-label="Select card"
      />
    </div>
  ) : null;

  return (
    <KanbanSortableItem
      id={item.id}
      data={{ itemId: item.id, columnId: item.columnId }}
      disabled={!cardDragEnabled}
      isDragSourceCollapsed={isDragSourceCollapsed}
      flipFrom={flipFrom}
    >
      <div className="kanban-card-slot" data-kanban-card-slot="">
        {renderCard(item, {
          density,
          isSelected,
          selectionEnabled: itemSelectable,
          onToggleSelect: toggleSelect,
          selectionControl,
          isOverlay: false,
          columnItemIds,
        })}
      </div>
    </KanbanSortableItem>
  );
}

function haveSameIds(previous: string[], next: string[]): boolean {
  return (
    previous.length === next.length &&
    previous.every((id, index) => id === next[index])
  );
}

function areCardItemPropsEqual<TItem extends KanbanItemBase>(
  previous: KanbanCardItemProps<TItem>,
  next: KanbanCardItemProps<TItem>
): boolean {
  return (
    previous.item === next.item &&
    previous.renderCard === next.renderCard &&
    previous.density === next.density &&
    previous.cardDragEnabled === next.cardDragEnabled &&
    previous.selectionEnabled === next.selectionEnabled &&
    previous.isItemSelectable === next.isItemSelectable &&
    previous.selectedIds === next.selectedIds &&
    previous.onSelectedIdsChange === next.onSelectedIdsChange &&
    previous.isDragSourceCollapsed === next.isDragSourceCollapsed &&
    previous.flipFrom === next.flipFrom &&
    haveSameIds(previous.columnItemIds, next.columnItemIds)
  );
}

export const KanbanCardItem = memo(
  KanbanCardItemInner,
  areCardItemPropsEqual
) as typeof KanbanCardItemInner;
